import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { registerPushNotifications } from '../lib/pushNotification';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const profileFetched = useRef(false);

  const fetchProfile = async (userId, userMeta, force = false, userEmail = '') => {
    // If we already have a profile loaded, don't fetch again unless forced
    if (profileFetched.current && !force) return;

    try {
      // Use a Promise.race timeout to absolutely guarantee it never hangs infinitely
      let timeoutId;
      const fetchPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
        
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          const err = new Error('Profile fetch timed out');
          err.name = 'TimeoutError';
          reject(err);
        }, 8000);
      });

      let data, error;
      try {
        const result = await Promise.race([fetchPromise, timeoutPromise]);
        data = result.data;
        error = result.error;
      } finally {
        clearTimeout(timeoutId);
      }

      // Check the new permanent user_roles table to see if they are an admin
      if (data && !error) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .maybeSingle();

        // Fall back to the old profiles column just in case the new table is empty
        data.is_admin = (roleData?.role === 'admin') || data.is_admin;
      }

      // Parse OAuth / User Metadata fields (handles Google, Yahoo, GitHub, etc.)
      const meta = { ...(userMeta || {}) };
      let metaFirstName = meta.first_name || meta.given_name || '';
      let metaLastName = meta.last_name || meta.family_name || '';

      const rawFullName = (meta.full_name || meta.name || meta.nickname || meta.preferred_username || '').trim();

      if ((!metaFirstName || !metaLastName) && rawFullName) {
        const parts = rawFullName.split(/\s+/);
        if (!metaFirstName) metaFirstName = parts[0] || '';
        if (!metaLastName) metaLastName = parts.slice(1).join(' ') || '';
      }

      // If first name is still empty, derive fallback names from user email (e.g. john.doe@yahoo.com -> John Doe)
      const userEmailAddress = meta.email || userEmail || '';
      if (!metaFirstName && userEmailAddress) {
        const rawPrefix = userEmailAddress.split('@')[0] || '';
        const cleanParts = rawPrefix.replace(/[._-]/g, ' ').trim().split(/\s+/);
        if (cleanParts[0]) {
          metaFirstName = cleanParts[0].charAt(0).toUpperCase() + cleanParts[0].slice(1);
        }
        if (cleanParts.length > 1 && !metaLastName) {
          metaLastName = cleanParts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
        }
      }

      const metaPhone = meta.phone || meta.phone_number || '';
      const metaDob = meta.dob || meta.birthdate || meta.date_of_birth || '';
      const metaAvatar = meta.avatar_url || meta.picture || '';

      if (error && error.code === 'PGRST116') {
        // Profile doesn't exist yet (e.g., Google/Yahoo OAuth user before trigger ran)
        const newProfile = {
          id: userId,
          first_name: metaFirstName,
          last_name: metaLastName,
          phone: metaPhone,
          date_of_birth: metaDob ? metaDob : null,
          avatar_url: metaAvatar,
        };

        const { data: inserted, error: insertError } = await supabase
          .from('profiles')
          .insert(newProfile)
          .select()
          .single();

        if (!insertError) {
          data = inserted;
        }
      } else if (data && !error) {
        // Profile exists. Auto-fill or repair missing/invalid ("null", "Unknown", empty) fields using OAuth/email details
        const updates = {};
        const isFnInvalid = !data.first_name || data.first_name.trim() === '' || data.first_name === 'null' || data.first_name === 'Unknown';
        const isLnInvalid = !data.last_name || data.last_name === 'null' || data.last_name === 'Unknown';

        if (isFnInvalid && metaFirstName) updates.first_name = metaFirstName;
        if (isLnInvalid && metaLastName) updates.last_name = metaLastName;
        if (!data.phone && metaPhone) updates.phone = metaPhone;
        if (!data.date_of_birth && metaDob) updates.date_of_birth = metaDob;
        if (!data.avatar_url && metaAvatar) updates.avatar_url = metaAvatar;

        if (Object.keys(updates).length > 0) {
          const { data: updated, error: updateErr } = await supabase
            .from('profiles')
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq('id', userId)
            .select()
            .single();

          if (!updateErr && updated) {
            data = { ...data, ...updated };
          }
        }
      }

      if (data) {
        setProfile(data);
        profileFetched.current = true;
      }
    } catch (err) {
      // If this was a timeout, log it quietly — the app will still work
      if (err.name === 'TimeoutError') {
        console.warn('Profile fetch timed out (Database query hung). App will continue.');
      } else {
        console.error('Error fetching profile:', err);
      }
    }
  };

  // Update profile in DB and local state
  const updateProfile = async (updates) => {
    if (!user) return { error: 'Not authenticated' };

    const { data, error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id)
      .select()
      .single();

    if (!error && data) {
      setProfile(data);
    }

    return { data, error };
  };

  useEffect(() => {
    let isMounted = true;
    let logoutTimer;

    const setupLogoutTimer = () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      // Log out after 1 hour (3600000 milliseconds)
      logoutTimer = setTimeout(() => {
        supabase.auth.signOut();
      }, 3600000);
    };

    // Get initial session
    const initSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) console.error("Error getting session:", error);

        const session = data?.session;
        if (isMounted) {
          setSession(session);
          setUser(session?.user ?? null);

          if (session?.user) {
            await fetchProfile(session.user.id, session.user.user_metadata, false, session.user.email);
            registerPushNotifications(session.user.id).catch(e => console.error(e));
            if (isMounted) setupLogoutTimer();
          }
        }
      } catch (err) {
        console.error("Unexpected error during initSession:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Only react to actual sign-in / sign-out. Ignore token refreshes & initial session.
      if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') return;

      if (isMounted) {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          // Reset the lock so a fresh sign-in can fetch the profile
          if (event === 'SIGNED_IN') profileFetched.current = false;
          await fetchProfile(session.user.id, session.user.user_metadata, false, session.user.email);
          registerPushNotifications(session.user.id).catch(e => console.error(e));
          if (isMounted) setupLogoutTimer();
        } else {
          setProfile(null);
          profileFetched.current = false;
          if (logoutTimer) clearTimeout(logoutTimer);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (logoutTimer) clearTimeout(logoutTimer);
    };
  }, []);

  // Set up real-time listener for profile updates in database
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`profile:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        () => {
          // Force fetch updated profile data when database updates (e.g. admin verifies payment)
          fetchProfile(user.id, null, true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id, null, true);
    }
  };

  const value = {
    session,
    user,
    profile,
    loading,
    updateProfile,
    refreshProfile,
    signOut: () => supabase.auth.signOut(),
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};
